import { LayoutDashboard, LogOut, PanelLeftClose, PanelLeftOpen, RotateCw, X } from "lucide-react";
import { useLanguage } from "../i18n/language-provider";
import type { PageName } from "../navigation/pages";
import { routePath, type AppRoute } from "../navigation/routes";
import { brandLabelToString, type BrandLabel } from "../theme/brand";
import { BrandHeading } from "./brand/BrandHeading";
import { AppLink } from "./navigation/AppLink";
import { RailFrame, railColumnClass } from "./RailFrame";
import { Button } from "./ui/button";

/**
 * What stands in the rail's place when the rail itself cannot be drawn.
 *
 * Each thing the rail draws from the server has a boundary of its own, so
 * what reaches the one around the whole rail (`App`) is a fault in what is
 * left: the navigation, the footer, a fixture's own shell. The page beside it
 * is unharmed and stays. The rail is how the user gets anywhere, though, so
 * its place is not left empty:
 *
 * - **It holds the rail's place** (`RailFrame`): the same column at the same
 *   width, folded or open, and in the phone's menu. The page is laid out
 *   against that width and does not move as the rail goes down or comes back.
 *   The header is the rail's own, fold toggle included: the toggle is what
 *   that header is as tall as, so the brand stays where the rail had it.
 * - **It says what happened and offers the reload**, which mends the likeliest
 *   cause, a page older than the server. Folded to icons, the reload names
 *   what it is for, and the toggle opens the stand-in to read the sentence.
 * - **It keeps the way home and the way out.** The dashboard is one click
 *   away, as a real link through the shell's own checks, and every page
 *   reached from there or by the address still works. Signing out is the one
 *   thing nothing else on the screen offers.
 *
 * Nothing more. Every further destination is another thing to go wrong on the
 * screen that stands when things have, and the reload brings the whole rail
 * back. It reads the words, the brand and what the shell hands it, and nothing
 * the rail fetched. The shell tries the rail again when the page or the
 * property changes (see the boundary in `App`), so this is gone by itself when
 * the fault was in what one of those drew.
 */

interface RailUnavailableProps {
    /** The page on screen, to mark the way home while it is the dashboard. */
    currentPage: PageName;
    /** The brand as the rail's header shows it. */
    brandLabel: BrandLabel;
    /** Whether the rail beside the page is folded to its icons. */
    collapsed: boolean;
    /** Fold or open the rail beside the page; the choice is the one the rail remembers. */
    onToggleCollapse?: () => void;
    /** Whether the phone's menu is open. */
    mobileOpen: boolean;
    onMobileClose: () => void;
    /** Leave for another screen; the shell decides whether it is open to this user. */
    onNavigate: (route: AppRoute) => void;
    onSignOut?: () => void;
}

/** A control in the rail's own colours, as every one on the rail wears them. */
const RAIL_CONTROL_CLASS = "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground";

export function RailUnavailable({
    currentPage,
    brandLabel,
    collapsed,
    onToggleCollapse,
    mobileOpen,
    onMobileClose,
    onNavigate,
    onSignOut,
}: RailUnavailableProps) {
    const { t } = useLanguage();
    const reloadLabel = t.app.pageLoadFailedReload;
    const reload = () => window.location.reload();
    const goHome = () => {
        onNavigate({ page: "dashboard" });
        onMobileClose();
    };
    const homeActive = currentPage === "dashboard";
    const homePath = routePath({ page: "dashboard" });

    const column = (folded: boolean) => (
        <div data-rail-unavailable className={railColumnClass(folded)}>
            <div data-rail-zone="head" className="bg-sidebar p-4 border-b border-sidebar-border flex items-center justify-between min-h-[57px]">
                {!folded && (
                    <AppLink
                        to={homePath}
                        onNavigate={goHome}
                        title={brandLabelToString(brandLabel)}
                        className="min-w-0 flex-1 text-sidebar-foreground hover:opacity-80 transition-opacity"
                    >
                        <BrandHeading label={brandLabel} />
                    </AppLink>
                )}
                {/* Both named and titled here, where the rail's own explain
                    themselves in a tooltip: one thing fewer to draw on this
                    screen. They read the rail's own words, so a control goes
                    by one name whether the rail or this is on screen. */}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onToggleCollapse}
                    aria-label={folded ? t.sidebar.expand : t.sidebar.collapse}
                    title={folded ? t.sidebar.expand : t.sidebar.collapse}
                    aria-expanded={!folded}
                    className={`hidden md:inline-flex flex-shrink-0 ${RAIL_CONTROL_CLASS}`}
                >
                    {folded ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
                </Button>
                {/* As large as the rail's own X, 2.5rem below `sm`: the header
                    is as tall as this control, and this holds the rail's
                    place. */}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onMobileClose}
                    aria-label={t.sidebar.closeMenu}
                    title={t.sidebar.closeMenu}
                    className={`md:hidden size-10 sm:size-9 flex-shrink-0 ${RAIL_CONTROL_CLASS}`}
                >
                    <X className="h-4 w-4" />
                </Button>
            </div>

            {folded ? (
                <div className="py-2 flex flex-col items-center border-b border-sidebar-border">
                    {/* No room for the sentence, so the reload carries it. */}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={reload}
                        aria-label={`${t.sidebar.unavailableTitle} · ${reloadLabel}`}
                        title={`${t.sidebar.unavailableTitle} · ${reloadLabel}`}
                        className={RAIL_CONTROL_CLASS}
                    >
                        <RotateCw className="h-4 w-4" />
                    </Button>
                </div>
            ) : (
                <div className="px-3 py-3 border-b border-sidebar-border space-y-2">
                    <div role="status" className="px-2">
                        <p className="text-sm text-sidebar-foreground">{t.sidebar.unavailableTitle}</p>
                        <p className="mt-0.5 text-xs text-sidebar-foreground/70">{t.sidebar.unavailableHint}</p>
                    </div>
                    <Button
                        variant="ghost"
                        onClick={reload}
                        // The hover colour is restated: a ghost button turns
                        // its text to the page's own, dark on this dark rail.
                        className="w-full justify-start gap-2 bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent/80 hover:text-sidebar-accent-foreground"
                    >
                        <RotateCw className="h-4 w-4" aria-hidden />
                        {reloadLabel}
                    </Button>
                </div>
            )}

            <nav className="flex-1 p-2 min-h-0 overflow-y-auto overflow-x-hidden">
                <Button
                    asChild
                    variant="ghost"
                    className={`w-full ${RAIL_CONTROL_CLASS} ${folded ? "justify-center px-2" : "justify-start"} ${homeActive ? "bg-accent text-primary" : ""}`}
                >
                    <AppLink
                        to={homePath}
                        active={homeActive}
                        onNavigate={goHome}
                        aria-label={folded ? t.sidebar.dashboard : undefined}
                        title={folded ? t.sidebar.dashboard : undefined}
                    >
                        <LayoutDashboard className={`h-4 w-4 flex-shrink-0 ${folded ? "" : "mr-3"}`} />
                        {!folded && <span className="truncate flex-1 text-left">{t.sidebar.dashboard}</span>}
                    </AppLink>
                </Button>
            </nav>

            {onSignOut && (
                <div className="p-2 border-t border-sidebar-border">
                    <Button
                        variant="ghost"
                        size={folded ? "icon" : "default"}
                        onClick={onSignOut}
                        aria-label={folded ? t.sidebar.signOut : undefined}
                        title={folded ? t.sidebar.signOut : undefined}
                        className={`${RAIL_CONTROL_CLASS} ${folded ? "mx-auto flex" : "w-full justify-start"}`}
                    >
                        <LogOut className={`h-4 w-4 flex-shrink-0 ${folded ? "" : "mr-3"}`} />
                        {!folded && t.sidebar.signOut}
                    </Button>
                </div>
            )}
        </div>
    );

    return (
        <RailFrame collapsed={collapsed} mobileOpen={mobileOpen} onMobileClose={onMobileClose}>
            {column}
        </RailFrame>
    );
}
